import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import UserAvatar, { UserAvatarGroup, UserBadge } from '../components/UserAvatar';
import { Users, Plus, Calendar as CalendarIcon, Clock, PlayCircle, CheckCircle2, Settings, UserPlus, Crown, Eye, Sparkles, Zap, Building, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSL10Meetings = () => {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [configureDialogOpen, setConfigureDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  // NEW: Instant meeting state
  const [newMeetingDialogOpen, setNewMeetingDialogOpen] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [instantMeetingLoading, setInstantMeetingLoading] = useState(false);
  const [isInPersonMeeting, setIsInPersonMeeting] = useState(true); // Default to in-person
  
  // Available participants and teams
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);

  // Meeting creation form
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    scheduled_date: '',
    duration_minutes: 90
  });
  
  // Meeting configuration form
  const [configForm, setConfigForm] = useState({
    team_ids: [],
    participant_ids: [],
    facilitator_id: ''
  });

  useEffect(() => {
    fetchMeetings();
    fetchAvailableParticipants();
  }, []);

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings`);
      setMeetings(response.data);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableParticipants = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/available-participants`);
      setAvailableUsers(response.data.users || []);
      setAvailableTeams(response.data.teams || []);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const handleCreateMeeting = async () => {
    try {
      if (!meetingForm.title || !meetingForm.scheduled_date) {
        toast.error('Please fill in title and date');
        return;
      }

      const payload = {
        title: meetingForm.title,
        scheduled_date: new Date(meetingForm.scheduled_date).toISOString(),
        duration_minutes: meetingForm.duration_minutes,
        participants: []
      };

      const response = await axios.post(`${API}/eos/meetings`, payload);
      toast.success('Meeting created successfully');
      setCreateDialogOpen(false);
      resetMeetingForm();
      fetchMeetings();
      
      // Open configuration dialog for the new meeting
      setSelectedMeeting(response.data);
      setConfigureDialogOpen(true);
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    }
  };
  
  const handleConfigureMeeting = async () => {
    try {
      if (!configForm.facilitator_id) {
        toast.error('Please select a facilitator');
        return;
      }
      
      if (configForm.participant_ids.length === 0) {
        toast.error('Please select at least one participant');
        return;
      }
      
      await axios.post(`${API}/eos/meetings/${selectedMeeting.id}/configure`, configForm);
      toast.success('Meeting configured successfully');
      setConfigureDialogOpen(false);
      fetchMeetings();
    } catch (error) {
      console.error('Error configuring meeting:', error);
      toast.error('Failed to configure meeting');
    }
  };
  
  const openConfigureDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setConfigForm({
      team_ids: meeting.team_ids || [],
      participant_ids: meeting.participants?.map(p => p.user_id || p) || [],
      facilitator_id: meeting.facilitator_id || ''
    });
    setConfigureDialogOpen(true);
  };

  const handleStartMeeting = (meetingId) => {
    navigate(`/eos/l10-meetings/${meetingId}`);
  };
  
  // NEW: Create and launch instant meeting
  const handleCreateInstantMeeting = async () => {
    if (selectedAttendees.length === 0) {
      toast.error('Please select at least one attendee');
      return;
    }
    
    setInstantMeetingLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      
      // Create meeting with current timestamp
      const payload = {
        title: `L10 Meeting - ${new Date().toLocaleDateString()}`,
        scheduled_date: new Date().toISOString(),
        duration_minutes: 90,
        status: 'in_progress',
        facilitator_id: currentUser?.user_id,
        is_in_person: isInPersonMeeting,
        attendees: selectedAttendees.map(u => ({
          user_id: u.id || u.user_id,
          // Use display_name from API, or build from first_name/last_name if available, or fallback to email
          display_name: u.display_name || 
            (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : null) || 
            u.email || 
            'Unknown User',
          email: u.email,
          status: 'present',
          department: u.department || '',
          role: u.role || '',
          first_name: u.first_name || '',
          last_name: u.last_name || ''
        }))
      };
      
      const response = await axios.post(`${API}/eos/meetings`, payload);
      const meetingId = response.data.id;
      
      // Only send notifications if NOT an in-person meeting
      if (!isInPersonMeeting) {
        try {
          await axios.post(`${API}/eos/meetings/${meetingId}/notify-attendees`, {
            message: `${currentUser?.first_name} ${currentUser?.last_name} has started an L10 meeting. Join now!`
          });
          toast.success('Meeting created! Notifications sent to attendees.');
        } catch (notifyError) {
          console.log('Notification endpoint not available, skipping');
          toast.success('Meeting created! Launching...');
        }
      } else {
        toast.success('In-person meeting started!');
      }
      
      setNewMeetingDialogOpen(false);
      setSelectedAttendees([]);
      setIsInPersonMeeting(true); // Reset for next time
      
      // Navigate to the meeting
      navigate(`/eos/l10-meetings/${meetingId}`);
    } catch (error) {
      console.error('Error creating instant meeting:', error);
      toast.error('Failed to create meeting');
    } finally {
      setInstantMeetingLoading(false);
    }
  };
  
  const toggleAttendeeSelection = (user) => {
    const userId = user.id || user.user_id;
    if (selectedAttendees.find(u => (u.id || u.user_id) === userId)) {
      setSelectedAttendees(selectedAttendees.filter(u => (u.id || u.user_id) !== userId));
    } else {
      setSelectedAttendees([...selectedAttendees, user]);
    }
  };
  
  const handleJoinAsObserver = async (meetingId) => {
    try {
      // Get current user info from localStorage
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      
      if (currentUser?.user_id) {
        await axios.post(`${API}/eos/meetings/${meetingId}/add-observer?user_id=${currentUser.user_id}`);
        toast.success('Joined as observer');
      }
      
      navigate(`/eos/l10-meetings/${meetingId}`);
    } catch (error) {
      console.error('Error joining as observer:', error);
      navigate(`/eos/l10-meetings/${meetingId}`);
    }
  };

  const resetMeetingForm = () => {
    setMeetingForm({
      title: '',
      scheduled_date: '',
      duration_minutes: 90
    });
  };
  
  const toggleParticipant = (userId) => {
    setConfigForm(prev => ({
      ...prev,
      participant_ids: prev.participant_ids.includes(userId)
        ? prev.participant_ids.filter(id => id !== userId)
        : [...prev.participant_ids, userId]
    }));
  };
  
  const toggleTeam = (teamId) => {
    const team = availableTeams.find(t => t.id === teamId);
    const teamMemberIds = availableUsers
      .filter(u => u.team_ids?.includes(teamId))
      .map(u => u.user_id);
    
    setConfigForm(prev => {
      const isSelected = prev.team_ids.includes(teamId);
      
      if (isSelected) {
        // Remove team and its members
        return {
          ...prev,
          team_ids: prev.team_ids.filter(id => id !== teamId),
          participant_ids: prev.participant_ids.filter(id => !teamMemberIds.includes(id))
        };
      } else {
        // Add team and its members
        return {
          ...prev,
          team_ids: [...prev.team_ids, teamId],
          participant_ids: [...new Set([...prev.participant_ids, ...teamMemberIds])]
        };
      }
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="outline">📅 Scheduled</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-600">⏱️ In Progress</Badge>;
      case 'complete':
        return <Badge className="bg-green-600">✅ Complete</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">❌ Cancelled</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Separate meetings by status
  const upcomingMeetings = meetings.filter(m => m.status === 'scheduled' && new Date(m.scheduled_date) > new Date());
  const inProgressMeetings = meetings.filter(m => m.status === 'in_progress');
  const completedMeetings = meetings.filter(m => m.status === 'complete').slice(0, 10);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Meetings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">L10 Meetings</h1>
              <p className="text-sm text-muted-foreground">
                Weekly leadership meetings for vision and traction
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => setNewMeetingDialogOpen(true)}>
              <Zap className="h-4 w-4 mr-2" />
              New Meeting
            </Button>
            <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Meeting
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{upcomingMeetings.length}</div>
                <div className="text-sm text-muted-foreground">Upcoming</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{inProgressMeetings.length}</div>
                <div className="text-sm text-muted-foreground">In Progress</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{completedMeetings.length}</div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* In Progress Meetings */}
        {inProgressMeetings.length > 0 && (
          <Card className="border-blue-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle className="text-blue-600">Meeting In Progress</CardTitle>
                  <CardDescription>Continue your active L10 meeting</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {inProgressMeetings.map(meeting => (
                  <Card key={meeting.id} className="border-blue-200 bg-blue-50 dark:bg-blue-950">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{meeting.title}</h3>
                            {getStatusBadge(meeting.status)}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span>📅 {formatDateTime(meeting.scheduled_date)}</span>
                            <span>⏱️ {meeting.duration_minutes} minutes</span>
                            {meeting.meeting_number && (
                              <span>Meeting #{meeting.meeting_number}</span>
                            )}
                          </div>
                          
                          {/* Facilitator & Participants */}
                          <div className="flex items-center gap-4 mt-2">
                            {meeting.facilitator_display && (
                              <div className="flex items-center gap-2">
                                <Crown className="h-4 w-4 text-yellow-500" />
                                <UserBadge user={meeting.facilitator_display} size="sm" />
                              </div>
                            )}
                            {meeting.participants?.length > 0 && (
                              <UserAvatarGroup users={meeting.participants} max={5} />
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => handleJoinAsObserver(meeting.id)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Observe
                          </Button>
                          <Button variant="outline" onClick={() => navigate(`/eos/l10-meetings/${meeting.id}/classic`)}>
                            Classic
                          </Button>
                          <Button onClick={() => handleStartMeeting(meeting.id)}>
                            <PlayCircle className="h-4 w-4 mr-2" />
                            Resume
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Meetings */}
        {upcomingMeetings.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle>Upcoming Meetings</CardTitle>
                  <CardDescription>{upcomingMeetings.length} scheduled</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {upcomingMeetings.map(meeting => (
                  <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{meeting.title}</h3>
                            {getStatusBadge(meeting.status)}
                            {(!meeting.participants || meeting.participants.length === 0) && (
                              <Badge variant="outline" className="text-orange-500 border-orange-500">
                                Needs Configuration
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>📅 {formatDateTime(meeting.scheduled_date)}</span>
                            <span>⏱️ {meeting.duration_minutes} minutes</span>
                            {meeting.meeting_number && (
                              <span>Meeting #{meeting.meeting_number}</span>
                            )}
                          </div>
                          
                          {/* Facilitator & Participants */}
                          <div className="flex items-center gap-4 mt-2">
                            {meeting.facilitator_display && (
                              <div className="flex items-center gap-2">
                                <Crown className="h-4 w-4 text-yellow-500" />
                                <UserBadge user={meeting.facilitator_display} size="sm" />
                              </div>
                            )}
                            {meeting.participants?.length > 0 && (
                              <UserAvatarGroup users={meeting.participants} max={5} />
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openConfigureDialog(meeting)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Configure
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/eos/l10-meetings/${meeting.id}/classic`)}>
                            Classic
                          </Button>
                          <Button onClick={() => handleStartMeeting(meeting.id)}>
                            <PlayCircle className="h-4 w-4 mr-2" />
                            Start
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed Meetings */}
        {completedMeetings.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle>Recent Meetings</CardTitle>
                  <CardDescription>Last 10 completed meetings</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {completedMeetings.map(meeting => (
                  <div 
                    key={meeting.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => handleStartMeeting(meeting.id)}
                  >
                    <div className="flex items-center gap-3">
                      {meeting.facilitator_display && (
                        <UserAvatar user={meeting.facilitator_display} size="sm" />
                      )}
                      <div>
                        <div className="font-medium">{meeting.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatDateTime(meeting.scheduled_date)}
                          {meeting.meeting_number && ` • Meeting #${meeting.meeting_number}`}
                          {meeting.total_duration_minutes && ` • ${meeting.total_duration_minutes} min`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {meeting.participants?.length > 0 && (
                        <UserAvatarGroup users={meeting.participants} max={3} size="xs" />
                      )}
                      {getStatusBadge(meeting.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {meetings.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No L10 meetings scheduled. Create your first meeting!</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Schedule Meeting
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Meeting Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule L10 Meeting</DialogTitle>
              <DialogDescription>Create a new weekly leadership meeting</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Meeting Title *</Label>
                <Input
                  placeholder="e.g., Leadership Team L10"
                  value={meetingForm.title}
                  onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                />
              </div>

              <div>
                <Label>Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={meetingForm.scheduled_date}
                  onChange={(e) => setMeetingForm({ ...meetingForm, scheduled_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={meetingForm.duration_minutes}
                  onChange={(e) => setMeetingForm({ ...meetingForm, duration_minutes: parseInt(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Standard L10 meeting is 90 minutes
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetMeetingForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateMeeting}>Create & Configure</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Configure Meeting Dialog */}
        <Dialog open={configureDialogOpen} onOpenChange={setConfigureDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Configure Meeting</DialogTitle>
              <DialogDescription>
                Select teams, participants, and assign a facilitator for {selectedMeeting?.title}
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="participants" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="teams">Teams</TabsTrigger>
                <TabsTrigger value="participants">Participants</TabsTrigger>
              </TabsList>
              
              <TabsContent value="teams" className="mt-4">
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-2">
                    {availableTeams.map(team => (
                      <div 
                        key={team.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          configForm.team_ids.includes(team.id) 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:bg-muted'
                        }`}
                        onClick={() => toggleTeam(team.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={configForm.team_ids.includes(team.id)}
                            onCheckedChange={() => toggleTeam(team.id)}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{team.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {team.member_count || 0} members • {team.department || 'No department'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {availableTeams.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No teams available</p>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="participants" className="mt-4">
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-2">
                    {availableUsers.map(user => (
                      <div 
                        key={user.user_id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          configForm.participant_ids.includes(user.user_id) 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:bg-muted'
                        }`}
                        onClick={() => toggleParticipant(user.user_id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={configForm.participant_ids.includes(user.user_id)}
                            onCheckedChange={() => toggleParticipant(user.user_id)}
                          />
                          <UserAvatar user={user} size="sm" showName />
                          <div className="flex-1 text-right">
                            <Badge variant="outline" className="text-xs">
                              {user.role?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {availableUsers.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No users available</p>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
            
            {/* Facilitator Selection */}
            <div className="mt-4 border-t pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <Crown className="h-4 w-4 text-yellow-500" />
                Select Facilitator *
              </Label>
              
              {configForm.participant_ids.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {configForm.participant_ids.map(userId => {
                    const user = availableUsers.find(u => u.user_id === userId);
                    if (!user) return null;
                    
                    return (
                      <div 
                        key={userId}
                        className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                          configForm.facilitator_id === userId 
                            ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950' 
                            : 'border-border hover:bg-muted'
                        }`}
                        onClick={() => setConfigForm(prev => ({ ...prev, facilitator_id: userId }))}
                      >
                        <div className="flex items-center gap-2">
                          <UserAvatar user={user} size="sm" />
                          <span className="text-sm font-medium truncate">{user.display_name}</span>
                          {configForm.facilitator_id === userId && (
                            <Crown className="h-4 w-4 text-yellow-500 ml-auto" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select participants first to choose a facilitator</p>
              )}
            </div>
            
            {/* Selected Summary */}
            <div className="flex items-center justify-between mt-4 p-3 bg-muted rounded-lg">
              <div className="text-sm">
                <span className="font-medium">{configForm.participant_ids.length}</span> participants selected
                {configForm.team_ids.length > 0 && (
                  <span className="text-muted-foreground"> from {configForm.team_ids.length} teams</span>
                )}
              </div>
              {configForm.facilitator_id && (
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-yellow-500" />
                  <UserBadge 
                    user={availableUsers.find(u => u.user_id === configForm.facilitator_id)} 
                    size="sm" 
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setConfigureDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfigureMeeting}>
                <UserPlus className="h-4 w-4 mr-2" />
                Save Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* NEW: Instant Meeting Dialog */}
        <Dialog open={newMeetingDialogOpen} onOpenChange={setNewMeetingDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-green-500" />
                Start New L10 Meeting
              </DialogTitle>
              <DialogDescription>
                Select attendees and launch the meeting immediately. Selected members will be notified.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* In-Person Meeting Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isInPersonMeeting ? 'bg-blue-500' : 'bg-slate-400'}`}>
                    {isInPersonMeeting ? <Building className="h-5 w-5 text-white" /> : <Wifi className="h-5 w-5 text-white" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{isInPersonMeeting ? 'In-Person Meeting' : 'Remote Meeting'}</p>
                    <p className="text-xs text-muted-foreground">
                      {isInPersonMeeting 
                        ? 'All attendees are in the room - no alerts sent' 
                        : 'Attendees will receive join notifications'}
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={isInPersonMeeting} 
                  onCheckedChange={setIsInPersonMeeting}
                />
              </div>
              
              <div>
                <Label className="text-sm mb-2 block">Select Attendees ({selectedAttendees.length} selected)</Label>
                <ScrollArea className="h-52 border rounded-lg p-2">
                  <div className="space-y-2">
                    {availableUsers.filter(u => u.is_active !== false).map(user => {
                      const userId = user.id || user.user_id;
                      const isSelected = selectedAttendees.find(u => (u.id || u.user_id) === userId);
                      
                      return (
                        <div
                          key={userId}
                          onClick={() => toggleAttendeeSelection(user)}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'bg-green-100 dark:bg-green-900/30 border border-green-500' : 'hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <UserAvatar user={user} size="sm" />
                            <div>
                              <p className="font-medium text-sm">{user.first_name} {user.last_name}</p>
                              <p className="text-xs text-muted-foreground">{user.role || user.email}</p>
                            </div>
                          </div>
                          <Checkbox checked={!!isSelected} />
                        </div>
                      );
                    })}
                    {availableUsers.filter(u => u.is_active !== false).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No team members available</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {selectedAttendees.length > 0 && (
                <div className={`p-3 rounded-lg border ${
                  isInPersonMeeting 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                }`}>
                  <p className={`text-sm ${isInPersonMeeting ? 'text-blue-700 dark:text-blue-400' : 'text-green-700 dark:text-green-400'}`}>
                    {isInPersonMeeting ? (
                      <>
                        <strong>{selectedAttendees.length}</strong> attendees marked as present. No notifications will be sent.
                      </>
                    ) : (
                      <>
                        <strong>{selectedAttendees.length}</strong> attendees will be notified and can join immediately.
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewMeetingDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleCreateInstantMeeting}
                disabled={selectedAttendees.length === 0 || instantMeetingLoading}
              >
                {instantMeetingLoading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Starting...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Start Meeting Now
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSL10Meetings;
